import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import DescriptionIcon from '@material-ui/icons/Description';
import RefreshIcon from '@material-ui/icons/Refresh';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SvgIcon from '@material-ui/core/SvgIcon';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { PodTerminal } from './PodTerminal';

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
  namespace: string;
}

interface PodInfo {
  name: string;
  status: string;
  ready: string;
  restarts: number;
  age: string;
  image: string;
  node: string;
  containers: string[];
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.2, 2),
    },
  },
  podName: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  image: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    maxWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    height: 500,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  logDialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logPodName: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  liveIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#4caf50',
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    animation: '$pulse 1.5s infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 },
  },
}));

function getPodStatusColor(status: string): string {
  if (status === 'Running') return '#4caf50';
  if (status === 'Succeeded') return '#4caf50';
  if (status === 'Pending') return '#ff9800';
  if (status === 'Failed' || status === 'CrashLoopBackOff' || status === 'Error') return '#f44336';
  if (status === 'Terminating') return '#9e9e9e';
  return '#9e9e9e';
}

function formatAge(creationTimestamp: string): string {
  const d = new Date(creationTimestamp);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function getPodStatus(pod: any): string {
  const containerStatuses = pod.status?.containerStatuses ?? [];
  for (const cs of containerStatuses) {
    if (cs.state?.waiting?.reason) return cs.state.waiting.reason;
    if (cs.state?.terminated?.reason) return cs.state.terminated.reason;
  }
  if (pod.metadata?.deletionTimestamp) return 'Terminating';
  return pod.status?.phase ?? 'Unknown';
}

function getPodReady(pod: any): string {
  const containerStatuses = pod.status?.containerStatuses ?? [];
  const total = containerStatuses.length || (pod.spec?.containers?.length ?? 0);
  const ready = containerStatuses.filter((cs: any) => cs.ready).length;
  return `${ready}/${total}`;
}

function getPodRestarts(pod: any): number {
  const containerStatuses = pod.status?.containerStatuses ?? [];
  return containerStatuses.reduce((sum: number, cs: any) => sum + (cs.restartCount ?? 0), 0);
}

function parsePods(data: any): PodInfo[] {
  return (data.items ?? []).map((pod: any) => ({
    name: pod.metadata?.name ?? '',
    status: getPodStatus(pod),
    ready: getPodReady(pod),
    restarts: getPodRestarts(pod),
    age: formatAge(pod.metadata?.creationTimestamp ?? ''),
    image: (pod.spec?.containers?.[0]?.image ?? '').split('/').pop() ?? '',
    node: (pod.spec?.nodeName ?? '').split('.')[0],
    containers: (pod.spec?.containers ?? []).map((c: any) => c.name as string),
  }));
}

export const InfrastructureContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const k8sId = annotations['backstage.io/kubernetes-id'] ?? entity.metadata.name;
  const labelSelector = annotations['backstage.io/kubernetes-label-selector'];

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [pods, setPods] = useState<Record<string, PodInfo[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Logs state
  const [logsPod, setLogsPod] = useState<{ name: string; namespace: string; containers: string[] } | null>(null);
  const [logsContainer, setLogsContainer] = useState('');
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLive, setLogsLive] = useState(true);
  const logsRef = useRef<HTMLDivElement>(null);
  const logsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTimestampRef = useRef<string>('');

  // Terminal state
  const [termPod, setTermPod] = useState<{ name: string; namespace: string; containers: string[] } | null>(null);
  const [termContainer, setTermContainer] = useState('');

  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
          namespace: c.getOptionalString('namespace') ?? 'default',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  useEffect(() => {
    if (!envs.length) return;
    envs.forEach(env => {
      setLoading(prev => ({ ...prev, [env.key]: true }));
      const selector = labelSelector ?? `app=${k8sId}`;
      fetchApi
        .fetch(
          `${backendBaseUrl}/api/proxy/kubernetes/api/api/v1/namespaces/${env.namespace}/pods?labelSelector=${encodeURIComponent(selector)}`,
        )
        .then(res => (res.ok ? res.json() : Promise.reject()))
        .then(data => {
          setPods(prev => ({ ...prev, [env.key]: parsePods(data) }));
          setLoading(prev => ({ ...prev, [env.key]: false }));
        })
        .catch(() => {
          setPods(prev => ({ ...prev, [env.key]: [] }));
          setLoading(prev => ({ ...prev, [env.key]: false }));
        });
    });
  }, [envs, k8sId, labelSelector, backendBaseUrl, fetchApi]);

  // ── Logs ──
  const fetchLogsOnce = useCallback(
    async (podName: string, namespace: string, container: string, since?: string) => {
      const params = new URLSearchParams({
        container,
        tailLines: since ? '500' : '200',
        timestamps: 'true',
      });
      if (since) params.set('sinceTime', since);

      const res = await fetchApi.fetch(
        `${backendBaseUrl}/api/proxy/kubernetes/api/api/v1/namespaces/${namespace}/pods/${podName}/log?${params}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    [backendBaseUrl, fetchApi],
  );

  const openLogs = useCallback(
    async (podName: string, namespace: string, containers: string[]) => {
      const container = containers[0] ?? '';
      setLogsPod({ name: podName, namespace, containers });
      setLogsContainer(container);
      setLogsContent('');
      setLogsLoading(true);
      setLogsLive(true);
      lastTimestampRef.current = '';
      try {
        const text = await fetchLogsOnce(podName, namespace, container);
        setLogsContent(text || '(aucun log)');
        // Extract last timestamp for incremental polling
        const lines = text.trim().split('\n');
        if (lines.length > 0) {
          const match = lines[lines.length - 1].match(/^(\S+)\s/);
          if (match) lastTimestampRef.current = match[1];
        }
      } catch (err: any) {
        setLogsContent(`Erreur: ${err.message}`);
      } finally {
        setLogsLoading(false);
      }
    },
    [fetchLogsOnce],
  );

  // Auto-scroll
  useEffect(() => {
    if (logsRef.current && logsLive) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [logsContent, logsLive]);

  // Live polling
  useEffect(() => {
    if (!logsPod || !logsLive || !logsContainer) return;
    const { name, namespace } = logsPod;
    const interval = setInterval(async () => {
      if (!lastTimestampRef.current) return;
      try {
        const text = await fetchLogsOnce(name, namespace, logsContainer, lastTimestampRef.current);
        if (text && text.trim()) {
          const lines = text.trim().split('\n');
          // Skip the first line if it's the same as our sinceTime (K8s includes it)
          const newLines = lines.filter(l => {
            const ts = l.match(/^(\S+)\s/)?.[1];
            return ts && ts > lastTimestampRef.current;
          });
          if (newLines.length > 0) {
            setLogsContent(prev => prev + '\n' + newLines.join('\n'));
            const lastMatch = newLines[newLines.length - 1].match(/^(\S+)\s/);
            if (lastMatch) lastTimestampRef.current = lastMatch[1];
          }
        }
      } catch { /* ignore polling errors */ }
    }, 3000);
    logsIntervalRef.current = interval;
    return () => clearInterval(interval);
  }, [logsPod, logsLive, logsContainer, fetchLogsOnce]);

  const switchLogsContainer = useCallback(
    async (container: string) => {
      setLogsContainer(container);
      setLogsContent('');
      setLogsLoading(true);
      lastTimestampRef.current = '';
      if (logsPod) {
        try {
          const text = await fetchLogsOnce(logsPod.name, logsPod.namespace, container);
          setLogsContent(text || '(aucun log)');
          const lines = text.trim().split('\n');
          if (lines.length > 0) {
            const match = lines[lines.length - 1].match(/^(\S+)\s/);
            if (match) lastTimestampRef.current = match[1];
          }
        } catch (err: any) {
          setLogsContent(`Erreur: ${err.message}`);
        } finally {
          setLogsLoading(false);
        }
      }
    },
    [logsPod, fetchLogsOnce],
  );

  const closeLogs = useCallback(() => {
    setLogsPod(null);
    setLogsContent('');
    if (logsIntervalRef.current) clearInterval(logsIntervalRef.current);
  }, []);

  // ── Terminal ──
  const openTerminal = useCallback((podName: string, namespace: string, containers: string[]) => {
    const container = containers[0] ?? '';
    setTermPod({ name: podName, namespace, containers });
    setTermContainer(container);
  }, []);

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const currentPods = pods[currentEnv.key] ?? [];
  const isLoading = loading[currentEnv.key] ?? true;

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(env => (
            <Tab
              key={env.key}
              label={`${env.displayName} (${env.namespace})`}
              style={{ color: env.color }}
            />
          ))}
        </Tabs>
        <CardContent style={{ padding: 0 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : currentPods.length === 0 ? (
            <Box className={classes.empty}>
              <Typography color="textSecondary">
                Aucun pod dans le namespace <strong>{currentEnv.namespace}</strong>
              </Typography>
            </Box>
          ) : (
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pod</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ready</TableCell>
                  <TableCell>Restarts</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      Image
                      <Tooltip title="Image du container principal" arrow>
                        <InfoOutlinedIcon style={{ fontSize: 14, color: '#9e9e9e' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPods.map(pod => (
                  <TableRow key={pod.name} hover>
                    <TableCell>
                      <Typography className={classes.podName}>{pod.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={pod.status}
                        style={{
                          backgroundColor: getPodStatusColor(pod.status),
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: 20,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{pod.ready}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{
                          color: pod.restarts > 5 ? '#f44336' : undefined,
                          fontWeight: pod.restarts > 5 ? 600 : undefined,
                        }}
                      >
                        {pod.restarts}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={pod.image} arrow>
                        <Typography className={classes.image}>{pod.image}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.date}>{pod.age}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" style={{ gap: 4 }}>
                        <Tooltip title="Logs" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openLogs(pod.name, currentEnv.namespace, pod.containers)}
                          >
                            <DescriptionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Terminal" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openTerminal(pod.name, currentEnv.namespace, pod.containers)}
                          >
                            <SvgIcon fontSize="small" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v12zM6 10l2.25 2.25L6 14.5l1 1 3.25-3.25L7 9l-1 1zm5 4.5h5v1.5h-5z" /></SvgIcon>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Logs Dialog ── */}
      <Dialog open={!!logsPod} onClose={closeLogs} maxWidth="lg" fullWidth>
        <DialogTitle disableTypography>
          <Box className={classes.logDialogTitle}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="h6">Logs</Typography>
              <Typography className={classes.logPodName}>{logsPod?.name}</Typography>
              {logsPod && logsPod.containers.length > 1 && (
                <FormControl size="small" style={{ minWidth: 140 }}>
                  <Select
                    value={logsContainer}
                    onChange={e => switchLogsContainer(e.target.value as string)}
                    variant="outlined"
                    style={{ fontSize: '0.8rem', height: 32 }}
                  >
                    {logsPod.containers.map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {logsLive && (
                <span className={classes.liveIndicator}>
                  <span className={classes.liveDot} />
                  LIVE
                </span>
              )}
              <Tooltip title={logsLive ? 'Mettre en pause' : 'Reprendre le live'} arrow>
                <IconButton size="small" onClick={() => setLogsLive(prev => !prev)}>
                  {logsLive ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Rafraîchir" arrow>
                <IconButton
                  size="small"
                  disabled={logsLoading}
                  onClick={() => logsPod && openLogs(logsPod.name, logsPod.namespace, logsPod.containers)}
                >
                  {logsLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div className={classes.logContainer} ref={logsRef}>
            {logsLoading ? 'Chargement...' : logsContent}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLogs}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Terminal Dialog ── */}
      <Dialog open={!!termPod} onClose={() => setTermPod(null)} maxWidth="lg" fullWidth>
        <DialogTitle disableTypography>
          <Box className={classes.logDialogTitle}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="h6">Terminal</Typography>
              <Typography className={classes.logPodName}>{termPod?.name}</Typography>
              {termPod && termPod.containers.length > 1 && (
                <FormControl size="small" style={{ minWidth: 140 }}>
                  <Select
                    value={termContainer}
                    onChange={e => setTermContainer(e.target.value as string)}
                    variant="outlined"
                    style={{ fontSize: '0.8rem', height: 32 }}
                  >
                    {termPod.containers.map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {termPod && (
            <PodTerminal
              backendBaseUrl={backendBaseUrl}
              namespace={termPod.namespace}
              podName={termPod.name}
              container={termContainer}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermPod(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
